import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CodeViewer from "@/components/CodeViewer";
import { api, API, getToken } from "@/lib/api";
import { TIMEFRAMES, defaultRule } from "@/lib/indicators";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { CompileCheckPanel, BacktestPanel, ShareModal } from "@/components/ProTools";
import { DeployModal } from "@/components/Deployments";
import { Panel, Field, Select } from "@/components/builder/BuilderPrimitives";
import { BuilderToolbar } from "@/components/builder/BuilderToolbar";
import { StrategyDoctorModal } from "@/components/builder/StrategyDoctorModal";
import { RulesPanel } from "@/components/builder/RulesPanel";
import { OrderExecutionPanel } from "@/components/builder/OrderExecutionPanel";

const emptyConfig = () => ({
  name: "Untitled Strategy",
  platform: "mt4",
  symbol: "EURUSD",
  timeframe: "PERIOD_H1",
  magic_number: 20260101,
  lot_size: 0.10,
  stop_loss_pips: 30,
  take_profit_pips: 60,
  trailing_stop_pips: 0,
  max_spread_pips: 0,
  entry_rules: [],
  exit_rules: [],
  // Order execution (pending orders)
  buy_order_type: "market",
  sell_order_type: "market",
  pending_offset_pips: 15,
  pending_anchor: null,
  pending_round_step_pips: 0,
  pending_expire_minutes: 60,
  oco_enabled: false,
});

export default function Builder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(emptyConfig());
  const [code, setCode] = useState("");
  const [strategyId, setStrategyId] = useState(id || null);
  const [busy, setBusy] = useState(false);
  const [initialLoad, setInitialLoad] = useState(!!id);
  const [compileResult, setCompileResult] = useState(null);
  const [backtestResult, setBacktestResult] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [showDeploy, setShowDeploy] = useState(false);
  const [showDoctor, setShowDoctor] = useState(false);
  const [proBusy, setProBusy] = useState(false);

  // Load existing strategy if editing
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data } = await api.get(`/strategies/${id}`);
        // Backfill stable ids on any rules loaded from the DB
        const withIds = (arr) => (arr || []).map((r) => r._rid ? r : { ...r, _rid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `r_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` });
        const cfgWithIds = { ...emptyConfig(), ...data.config, name: data.name || data.config?.name || "Untitled" };
        cfgWithIds.entry_rules = withIds(cfgWithIds.entry_rules);
        cfgWithIds.exit_rules = withIds(cfgWithIds.exit_rules);
        setCfg(cfgWithIds);
        setCode(data.mql4_code || "");
        setStrategyId(data.strategy_id);
      } catch {
        toast.error("Strategy not found");
        nav("/dashboard");
      } finally { setInitialLoad(false); }
    })();
  }, [id, nav]);

  // Debounced live preview regeneration
  const regenerate = useCallback(async (config) => {
    try {
      const { data } = await api.post("/generate/visual", { config });
      setCode(data.mql4_code);
    } catch (err) {
      console.debug("[builder] live regenerate failed (ignored)", err?.response?.status || err);
    }
  }, []);

  useEffect(() => {
    if (initialLoad) return;
    const t = setTimeout(() => regenerate({ ...cfg }), 350);
    return () => clearTimeout(t);
  }, [cfg, regenerate, initialLoad]);

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const addRule = (kind) => setCfg((c) => ({
    ...c, [kind]: [...c[kind], defaultRule("RSI", "buy")]
  }));

  const removeRule = (kind, idx) => setCfg((c) => {
    const arr = [...c[kind]]; arr.splice(idx, 1); return { ...c, [kind]: arr };
  });

  const updateRule = (kind, idx, patch) => setCfg((c) => {
    const arr = [...c[kind]];
    const cur = { ...arr[idx], ...patch };
    // if indicator changed, reset with defaults for that indicator (keep stable id)
    if (patch.indicator && patch.indicator !== arr[idx].indicator) {
      const nd = defaultRule(patch.indicator, cur.direction || "buy");
      arr[idx] = { ...nd, _rid: arr[idx]._rid };
    } else {
      arr[idx] = cur;
    }
    return { ...c, [kind]: arr };
  });

  const save = async () => {
    setBusy(true);
    try {
      const payload = { name: cfg.name, source: "visual", config: cfg, mql4_code: code };
      if (strategyId) {
        await api.put(`/strategies/${strategyId}`, { name: cfg.name, config: cfg });
        toast.success("Strategy saved");
      } else {
        const { data } = await api.post("/strategies", payload);
        setStrategyId(data.strategy_id);
        window.history.replaceState(null, "", `/builder/${data.strategy_id}`);
        toast.success("Strategy created");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const download = async () => {
    const ext = cfg.platform === "mt5" ? "mq5" : "mq4";
    if (!strategyId) {
      // fallback to client blob
      const blob = new Blob([code], { type: `text/x-${ext}` });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (cfg.name || "Strategy").replace(/[^A-Za-z0-9_]+/g, "_");
      a.href = url; a.download = `${safe}.${ext}`; a.click();
      URL.revokeObjectURL(url);
      return;
    }
    const token = getToken();
    const res = await fetch(`${API}/strategies/${strategyId}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    const text = await res.text();
    const blob = new Blob([text], { type: `text/x-${ext}` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = (cfg.name || "Strategy").replace(/[^A-Za-z0-9_]+/g, "_");
    a.href = url; a.download = `${safe}.${ext}`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded");
  };

  const filename = useMemo(() => {
    const safe = (cfg.name || "Strategy").replace(/[^A-Za-z0-9_]+/g, "_");
    const ext = cfg.platform === "mt5" ? "mq5" : "mq4";
    return `${safe}.${ext}`;
  }, [cfg.name, cfg.platform]);

  const runCompileCheck = async () => {
    setProBusy(true);
    try {
      const url = strategyId
        ? `/strategies/${strategyId}/compile-check`
        : `/compile-check`;
      const { data } = strategyId
        ? await api.post(url)
        : await api.post(url, { mql4_code: code });
      // Local check has no `source` field — label it so the UI panel is clear.
      if (!data.source) data.source = "regex_local";
      setCompileResult(data);
      setBacktestResult(null);
      toast[data.ok ? "success" : "error"](data.ok ? "Compile check passed" : `${data.error_count} errors`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compile check failed");
    } finally { setProBusy(false); }
  };

  const runCompileMT = async () => {
    setProBusy(true);
    setCompileResult(null);
    try {
      const platform = cfg.platform === "mt5" ? "mt5" : "mt4";
      const { data: job } = await api.post("/compile-jobs", { mql4_code: code, platform });
      toast.info("Submitted to MetaEditor worker — polling…");
      // Poll every 2s, up to ~40s (backend fallback triggers at 30s).
      const start = Date.now();
      let finalResult = null;
      while (Date.now() - start < 45_000) {
        await new Promise(r => setTimeout(r, 2000));
        const { data: poll } = await api.get(`/compile-jobs/${job.job_id}`);
        if (poll.status === "complete" || poll.status === "fallback") {
          finalResult = poll.result;
          break;
        }
      }
      if (!finalResult) {
        toast.error("MetaEditor compile-check timed out");
        return;
      }
      setCompileResult(finalResult);
      setBacktestResult(null);
      if (finalResult.source === "metaeditor_worker") {
        toast.success("MetaEditor compile OK");
      } else {
        toast.warning("No MT worker responded — used regex fallback");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "MetaEditor compile failed");
    } finally { setProBusy(false); }
  };

  const runBacktest = async () => {
    setProBusy(true);
    try {
      const url = strategyId ? `/strategies/${strategyId}/backtest` : `/backtest`;
      const { data } = strategyId ? await api.post(url) : await api.post(url, { config: cfg });
      setBacktestResult(data);
      setCompileResult(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backtest failed");
    } finally { setProBusy(false); }
  };

  const runRealBacktest = async () => {
    if (!strategyId) { toast.error("Save the strategy first"); return; }
    // Pick a broker connection
    try {
      const { data: conns } = await api.get("/broker/connections");
      if (conns.length === 0) { toast.error("Add a broker connection at /broker first"); return; }
      const cid = conns[0].connection_id;
      setProBusy(true);
      const { data } = await api.post(`/strategies/${strategyId}/backtest-real`, {
        connection_id: cid, n_candles: 500,
      });
      setBacktestResult(data);
      setCompileResult(null);
      toast.success(`Real backtest complete (${data.candles} bars)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Real backtest failed");
    } finally { setProBusy(false); }
  };

  const openShare = () => {
    if (!strategyId) { toast.error("Save the strategy first"); return; }
    setShowShare(true);
  };

  const openDeploy = () => {
    if (!strategyId) { toast.error("Save the strategy first"); return; }
    setShowDeploy(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="border-b border-white/5 bg-black/30">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex flex-wrap items-center gap-3">
          <BuilderToolbar
            cfg={cfg}
            setField={setField}
            strategyId={strategyId}
            busy={busy}
            proBusy={proBusy}
            onCompile={runCompileCheck}
            onCompileMT={runCompileMT}
            onBacktest={runBacktest}
            onBacktestReal={runRealBacktest}
            onShare={openShare}
            onDeploy={openDeploy}
            onSave={save}
            onDownload={download}
            onDoctor={strategyId ? (()=>setShowDoctor(true)) : undefined}
          />
        </div>
      </div>

      <main className="flex-1 max-w-[1400px] w-full mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: Config */}
        <section className="lg:col-span-7 space-y-6">
          <Panel title="Instrument & Risk">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Symbol">
                <input data-testid="cfg-symbol" className="input-dark" value={cfg.symbol}
                  onChange={(e)=>setField("symbol", e.target.value.toUpperCase())}/>
              </Field>
              <Field label="Timeframe">
                <Select data-testid="cfg-timeframe" value={cfg.timeframe}
                  onChange={(v)=>setField("timeframe", v)}
                  options={TIMEFRAMES.map(t => ({v:t, l:t.replace("PERIOD_","")}))}/>
              </Field>
              <Field label="Lot size">
                <input data-testid="cfg-lot" type="number" step="0.01" min="0.01" className="input-dark" value={cfg.lot_size}
                  onChange={(e)=>setField("lot_size", parseFloat(e.target.value)||0)}/>
              </Field>
              <Field label="Magic number">
                <input data-testid="cfg-magic" type="number" className="input-dark" value={cfg.magic_number}
                  onChange={(e)=>setField("magic_number", parseInt(e.target.value)||0)}/>
              </Field>
              <Field label="Stop loss (pips)">
                <input data-testid="cfg-sl" type="number" className="input-dark" value={cfg.stop_loss_pips}
                  onChange={(e)=>setField("stop_loss_pips", parseInt(e.target.value)||0)}/>
              </Field>
              <Field label="Take profit (pips)">
                <input data-testid="cfg-tp" type="number" className="input-dark" value={cfg.take_profit_pips}
                  onChange={(e)=>setField("take_profit_pips", parseInt(e.target.value)||0)}/>
              </Field>
              <Field label="Trailing stop (pips)">
                <input data-testid="cfg-ts" type="number" className="input-dark" value={cfg.trailing_stop_pips}
                  onChange={(e)=>setField("trailing_stop_pips", parseInt(e.target.value)||0)}/>
              </Field>
              <Field label="Max spread (pips)">
                <input data-testid="cfg-spread" type="number" className="input-dark" value={cfg.max_spread_pips}
                  onChange={(e)=>setField("max_spread_pips", parseInt(e.target.value)||0)}/>
              </Field>
            </div>
          </Panel>

          <OrderExecutionPanel cfg={cfg} setCfg={setCfg} setField={setField}/>

          <RulesPanel
            testIdPrefix="entry"
            title="Entry Rules"
            hint="All rules combined with AND per direction (buy/sell). Add multiple to compose."
            rules={cfg.entry_rules}
            onAdd={()=>addRule("entry_rules")}
            onRemove={(i)=>removeRule("entry_rules", i)}
            onUpdate={(i,p)=>updateRule("entry_rules", i, p)}
            accent="profit"
          />

          <RulesPanel
            testIdPrefix="exit"
            title="Exit Rules"
            hint="Optional. When any rule for a direction is true, that side is closed."
            rules={cfg.exit_rules}
            onAdd={()=>addRule("exit_rules")}
            onRemove={(i)=>removeRule("exit_rules", i)}
            onUpdate={(i,p)=>updateRule("exit_rules", i, p)}
            accent="loss"
          />
        </section>

        {/* RIGHT: Code Preview */}
        <aside className="lg:col-span-5 lg:sticky lg:top-20 h-fit space-y-4">
          {compileResult && <CompileCheckPanel result={compileResult} onClose={()=>setCompileResult(null)}/>}
          {backtestResult && <BacktestPanel result={backtestResult} onClose={()=>setBacktestResult(null)}/>}
          <div className="card-sharp overflow-hidden">
            <CodeViewer code={code} filename={filename} onDownload={download}/>
          </div>
          <p className="text-[11px] font-mono text-[var(--text-muted)] flex items-center gap-1.5">
            <RefreshCw size={11}/> Auto-regenerates as you edit.
          </p>
        </aside>
      </main>
      <ShareModal open={showShare} strategyId={strategyId} strategyName={cfg.name}
        onClose={()=>setShowShare(false)}/>
      {showDeploy && strategyId && (
        <DeployModal
          strategy={{ strategy_id: strategyId, name: cfg.name }}
          onClose={()=>setShowDeploy(false)}
          onDeployed={(d) => { setShowDeploy(false); nav(`/deployments/${d.deployment_id}`); }}
        />
      )}
      {showDoctor && strategyId && (
        <StrategyDoctorModal strategyId={strategyId} onClose={()=>setShowDoctor(false)}/>
      )}
    </div>
  );
}
